import React, { useState, useEffect } from 'react';
import { apiService, SystemStatus } from '../services/api';
// import KillSwitch from './KillSwitch';
import PositionDisplay from './PositionDisplay';
import TradeHistory from './TradeHistory';
import StatusIndicator from './StatusIndicator';
import TradingLogs from './TradingLogs';

interface DashboardProps {
  onLogout?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(0);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await apiService.getStatus();
      setStatus(data);
      setError(null);
      setLastUpdate(new Date());
      setHistoryRefreshTrigger((t) => t + 1);
    } catch (err) {
      setError('Failed to fetch status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !status) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-700 border-t-blue-500 mx-auto"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 bg-blue-500 rounded-full animate-pulse"></div>
            </div>
          </div>
          <p className="mt-6 text-slate-300 text-lg font-medium">Loading Dashboard...</p>
          <p className="mt-2 text-slate-500 text-sm">Connecting to trading system</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Token expired / invalid alert from /status tokenValid */}
      {status?.tokenValid === false && (
        <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border-b border-red-500/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-red-300">
                  Schwab token expired or invalid
                </p>
                <p className="text-xs text-red-400/90 mt-0.5">
                  Automatic trading may fail. Please reconnect in Admin → Configuration → Token Management to get a new token.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Simulation Mode Banner */}
      {status?.simulationMode && (
        <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-b border-yellow-500/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center justify-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-yellow-300">
                  🔧 SIMULATION MODE ACTIVE
                </p>
                <p className="text-xs text-yellow-400/80 mt-0.5">
                  No real orders will be placed • All trades are simulated
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Logo + app name — centered, logo shows when /logo.png exists */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4">
            <img
              src="/images.png"
              alt=""
              className="flex-shrink-0"
              style={{
                maxHeight: '64px',
                width: 'auto',
                maxWidth: '180px',
                objectFit: 'contain',
              }}
              onError={(e) => {
                const el = e.target as HTMLImageElement;
                el.style.display = 'none';
              }}
            />
            <div className="text-center sm:text-left">
              <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                Momentum Inverse Model
              </h1>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Trading Automation
              </h2>
              <p className="text-slate-400 text-sm mt-1">System status & position</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-xs text-slate-500">Last Update</p>
                <p className="text-sm text-slate-300 font-mono">
                  {lastUpdate.toLocaleTimeString()}
                </p>
              </div>
              <button
                onClick={fetchStatus}
                disabled={loading}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh</span>
              </button>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="px-4 py-2 bg-slate-700 hover:bg-red-600/80 text-slate-200 rounded-lg text-sm font-medium transition-colors"
                >
                  Sign out
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg backdrop-blur-sm flex items-center space-x-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <StatusIndicator status={status} />
          {/* <KillSwitch 
            enabled={status?.killSwitchEnabled || false}
            onToggle={fetchStatus}
          /> */}
          <PositionDisplay 
            position={status?.currentPosition ?? null}
            balance={status?.accountBalance || 0}
          />
        </div>

        {/* Trade History */}
        <TradeHistory refreshTrigger={historyRefreshTrigger} />

        {/* Trading Logs (market open/close events) */}
        <div className="mt-8">
          <TradingLogs />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
